import type { StatModifier } from '../stats/StatSheet';
import type { Profile, ProfileData } from './Profile';

/**
 * Permanent shard-bought upgrade tracks (revised meta pillar, July 2026):
 * capped power is allowed — each track has a HARD max rank, so grinding buys
 * a fixed head start, never an open-ended one. Every track feeds the run as
 * a 'meta' StatSheet source (zeal/communion ride the killFillMult /
 * pickupRateMult stats — no token plumbing). The retired foresight/refusal
 * token tracks are refunded as shards on profile load (Profile.ts).
 */

export type MetaUpgradeId = 'vitality' | 'ferocity' | 'attunement' | 'zeal' | 'communion';

export interface MetaUpgradeDef {
  id: MetaUpgradeId;
  name: string;
  maxRank: number;
  /** costs[r] = shards for rank r+1; length === maxRank. */
  costs: readonly number[];
  /** Per-rank effect, display form ("+1 max HP"). */
  perRank: string;
  /** Stat effect per rank; null for token tracks (foresight/refusal). */
  statPerRank: StatModifier | null;
}

export const META_UPGRADES: readonly MetaUpgradeDef[] = [
  {
    id: 'vitality', name: 'Vitality', maxRank: 3, costs: [60, 120, 200],
    perRank: '+1 max HP', statPerRank: { stat: 'maxHp', flat: 1 },
  },
  {
    id: 'ferocity', name: 'Ferocity', maxRank: 3, costs: [80, 160, 260],
    perRank: '+5% damage', statPerRank: { stat: 'damageMult', pct: 0.05 },
  },
  {
    id: 'attunement', name: 'Attunement', maxRank: 3, costs: [40, 80, 140],
    perRank: '+20 magnet range', statPerRank: { stat: 'magnetRange', flat: 20 },
  },
  {
    id: 'zeal', name: 'Zeal', maxRank: 3, costs: [70, 150, 240],
    perRank: '+4% bar fill', statPerRank: { stat: 'killFillMult', pct: 0.04 },
  },
  {
    id: 'communion', name: 'Communion', maxRank: 3, costs: [50, 110, 180],
    perRank: '+6% gem stat gains', statPerRank: { stat: 'pickupRateMult', pct: 0.06 },
  },
];

export const META_UPGRADES_BY_ID: ReadonlyMap<MetaUpgradeId, MetaUpgradeDef> =
  new Map(META_UPGRADES.map((d) => [d.id, d]));

/** Rank clamped to [0, maxRank] — tolerates missing/garbage stored values. */
export function metaUpgradeRank(data: ProfileData, id: MetaUpgradeId): number {
  const def = META_UPGRADES_BY_ID.get(id);
  if (!def) return 0;
  const raw = data.upgrades[id];
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return 0;
  return Math.min(def.maxRank, Math.max(0, Math.floor(raw)));
}

/** Shard cost of the next rank, or null at cap. */
export function metaUpgradeCost(data: ProfileData, id: MetaUpgradeId): number | null {
  const def = META_UPGRADES_BY_ID.get(id);
  if (!def) return null;
  return def.costs[metaUpgradeRank(data, id)] ?? null;
}

export type MetaUpgradeCheck = 'ok' | 'atCap' | 'broke';

export function canBuyMetaUpgrade(profile: Profile, id: MetaUpgradeId): MetaUpgradeCheck {
  const cost = metaUpgradeCost(profile.data, id);
  if (cost === null) return 'atCap';
  if (profile.data.shards < cost) return 'broke';
  return 'ok';
}

/** Spend shards for the next rank. Saves on success (à la upgradeArtifact). */
export function buyMetaUpgrade(profile: Profile, id: MetaUpgradeId): MetaUpgradeCheck {
  const check = canBuyMetaUpgrade(profile, id);
  if (check !== 'ok') return check;
  const cost = metaUpgradeCost(profile.data, id)!;
  profile.data.shards -= cost;
  profile.data.upgrades[id] = metaUpgradeRank(profile.data, id) + 1;
  profile.save();
  return 'ok';
}

/** Resolved stat modifiers for the profile's ranks — RunState's 'meta' source. */
export function metaStatModifiers(data: ProfileData): StatModifier[] {
  const mods: StatModifier[] = [];
  for (const def of META_UPGRADES) {
    if (def.statPerRank === null) continue;
    const rank = metaUpgradeRank(data, def.id);
    if (rank === 0) continue;
    mods.push({
      stat: def.statPerRank.stat,
      ...(def.statPerRank.flat !== undefined ? { flat: def.statPerRank.flat * rank } : {}),
      ...(def.statPerRank.pct !== undefined ? { pct: def.statPerRank.pct * rank } : {}),
    });
  }
  return mods;
}
